import posthog from "posthog-js"

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || "https://app.posthog.com"

function enabled() {
  return Boolean(POSTHOG_KEY) && POSTHOG_KEY !== "your-posthog-key"
}

export function initPostHog() {
  if (!enabled()) {
    console.log("[PostHog] No key configured — skipping init")
    return
  }

  posthog.init(POSTHOG_KEY as string, {
    api_host: POSTHOG_HOST,
    capture_pageview: true, // auto page views
    capture_pageleave: true, // track when users leave
    autocapture: true, // auto-capture clicks/inputs
    session_recording: {
      maskAllInputs: true, // privacy: mask passwords etc
      maskInputOptions: {
        password: true,
        email: false, // show email for debugging
      },
    },
    persistence: "localStorage",
    loaded: (ph) => {
      if (import.meta.env.DEV) {
        ph.opt_out_capturing() // don't track in development
        console.log("[PostHog] Dev mode — tracking disabled")
      }
    },
  })
}

// Identify user when they log in
export function identifyUser(user: {
  id: string
  email: string
  fullName: string
  role: "CLIENT_ADMIN" | "SUPER_ADMIN" | "PROCTOR" | "CANDIDATE"
  companyName?: string
}) {
  if (!enabled()) return
  posthog.identify(user.id, {
    email: user.email,
    name: user.fullName,
    role: user.role,
    company: user.companyName,
  })
}

// Reset on logout
export function resetUser() {
  if (!enabled()) return
  posthog.reset()
}

// Track custom events
export function track(event: string, properties?: Record<string, unknown>) {
  if (!enabled()) return
  posthog.capture(event, properties)
}

// Typed event tracking functions
export const analytics = {
  // Assessment events
  assessmentCreated: (props: { assessmentId: string; name: string; projectId: string }) =>
    track("Assessment Created", props),

  assessmentPublished: (props: {
    assessmentId: string
    name: string
    questionCount: number
    candidateCount: number
  }) => track("Assessment Published", props),

  assessmentUnpublished: (props: { assessmentId: string; name: string }) =>
    track("Assessment Unpublished", props),

  // Candidate events
  candidateInvited: (props: {
    assessmentId: string
    assessmentName: string
    count: number
  }) => track("Candidate Invited", props),

  // Test events
  testStarted: (props: {
    assessmentId: string
    assessmentName: string
    candidateId: string
  }) => track("Test Started", props),

  testSubmitted: (props: {
    assessmentId: string
    assessmentName: string
    score: number
    passed: boolean
    durationSeconds: number
  }) => track("Test Submitted", props),

  // Report events
  reportViewed: (props: { attemptId: string; assessmentName: string }) =>
    track("Report Viewed", props),

  reportDownloaded: (props: { attemptId: string; format: "pdf" }) =>
    track("Report Downloaded", props),

  // Proctoring events
  proctorJoined: (props: { assessmentId: string; candidateCount: number }) =>
    track("Proctor Joined Live Feed", props),

  snapshotTaken: (props: { attemptId: string; source: "WEBCAM" | "SCREEN" }) =>
    track("Snapshot Taken", props),

  flagRaised: (props: { attemptId: string; flagType: string; severity: string }) =>
    track("Flag Raised", props),

  // Multirater events
  multiraterCreated: (props: { type: string; subjectCount: number }) =>
    track("Multirater Created", props),

  multiraterReportViewed: (props: { multirateId: string; degree: string }) =>
    track("Multirater Report Viewed", props),

  // Auth events
  loginSuccess: (props: { role: string }) => track("Login Success", props),

  loginFailed: (props: { role: string; reason?: string }) =>
    track("Login Failed", props),

  // UI events
  darkModeToggled: (props: { isDark: boolean }) => track("Dark Mode Toggled", props),

  questionTypeUsed: (props: { questionType: string; assessmentId: string }) =>
    track("Question Type Used", props),
}

export default posthog
